/**
 * NEXORA AI - CompleteStep (Refactorisé Final avec Design System)
 * Epic 1.3 Sprint Final - Clôture Onboarding Premium
 * 
 * Écran final d'accomplissement avec animations Premium
 * Standard : Framer Motion, Typography conforme, Message PO
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Sparkles, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';

export default function CompleteStep({ onComplete }) {
  const { t } = useTranslation('onboarding');
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Déclencher animation de célébration après 300ms
    setTimeout(() => setShowConfetti(true), 300);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 relative overflow-hidden">
      {/* Background effects - Subtle gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="absolute top-20 left-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 0.2, scale: 1 }}
          transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-purple-600/15 rounded-full blur-3xl"
        />
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 text-center max-w-2xl"
      >
        {/* Success Icon avec animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', duration: 0.8, delay: 0.2 }}
          className="mb-8 inline-block"
        >
          <div className="w-28 h-28 sm:w-32 sm:h-32 mx-auto rounded-full bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center shadow-2xl shadow-green-500/30">
            <CheckCircle className="w-14 h-14 sm:w-16 sm:h-16 text-white" strokeWidth={2} />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight text-white mb-4 sm:mb-5"
        >
          {t('complete_title')}
        </motion.h1>
        
        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.4 }}
          className="text-base md:text-lg text-zinc-300 mb-3 sm:mb-4"
        >
          {t('complete_subtitle')}
        </motion.p>
        
        {/* Message de valorisation final (PO) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="mb-10 sm:mb-12"
        >
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30">
            <Sparkles className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <p className="text-sm sm:text-base text-zinc-300 max-w-lg">
              {t('validation_complete')}
            </p>
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
        >
          <Button
            size="lg"
            onClick={onComplete}
            className="group min-h-[44px] px-8 sm:px-10 text-base sm:text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 shadow-lg shadow-green-500/30 transition-all duration-300"
            data-testid="go-to-dashboard"
          >
            <Sparkles className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-200" />
            {t('go_to_dashboard')}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
          </Button>
        </motion.div>

        {/* Subtle sparkle animations (optional, élégant) */}
        {showConfetti && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: [0, 1, 0], y: -100 }}
              transition={{ duration: 2, delay: 0.8 }}
              className="absolute top-1/4 left-1/4 text-4xl"
            >
              ✨
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: [0, 1, 0], y: -120 }}
              transition={{ duration: 2.2, delay: 1 }}
              className="absolute top-1/3 right-1/4 text-3xl"
            >
              🎉
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: [0, 1, 0], y: -90 }}
              transition={{ duration: 1.8, delay: 1.2 }}
              className="absolute bottom-1/3 left-1/3 text-3xl"
            >
              ⭐
            </motion.div>
          </>
        )}
      </motion.div>
    </div>
  );
}
